<script setup>

import {ref, watch} from "vue";

const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    uuid: {
        type: String,
        required: true
    },
    content: {
        type: Object,
        required: true,
    }
})

const course = ref(null);
const courseLoaded = ref(false);
const currentPage = ref(1);
const lastPage = ref(1);
const pageBuilderActive = window.myflow.pagebuilderActive

const normalizedPerPage = () => {
    const parsed = Number.parseInt(props.content?.perPage, 10);

    if (Number.isNaN(parsed)) {
        return 10;
    }

    return Math.max(1, Math.min(50, parsed));
};

const loadCourse = async (page = currentPage.value) => {
    if (!props.content.courseId) {
        course.value = null;
        courseLoaded.value = true;
        currentPage.value = 1;
        lastPage.value = 1;

        return;
    }

    courseLoaded.value = false;

    try {
        const res = await axios.get(route('api.courses.feed.customerarea.show', props.content.courseId), {
            params: {
                page,
                per_page: normalizedPerPage(),
            }
        });
        if (res.data.success)    {
            course.value = res.data.course
            currentPage.value = res.data.course?.pagination?.current_page ?? page;
            lastPage.value = res.data.course?.pagination?.last_page ?? 1;
        } else {
            course.value = null;
        }
    } catch (error) {
        course.value = null
        currentPage.value = 1;
        lastPage.value = 1;
    }
    courseLoaded.value = true
};

const changePage = (page) => {
    if (page < 1 || page > lastPage.value || page === currentPage.value) {
        return;
    }

    loadCourse(page);
};

watch([() => props.content.courseId, () => props.content.perPage], () => {
    currentPage.value = 1;
    loadCourse(1);
}, { immediate: true });

</script>

<template>
    <div>
        <div>
            <div v-if="course">
                <ul  :class="content.displayMode === 'grid' ? 'grid grid-cols-2 gap-4 mt-2' : ''">
                    <li
                        v-if="course.chapters.length === 0"
                        class="text-center"
                    >
                        {{ _mft('course:feed.noContent')}}
                    </li>
                    <li
                        v-for="(chapter, i) in course.chapters"
                        :key="i"
                        class="flex"
                        :class="content.displayMode === 'grid' ? 'flex flex-col rounded-wl overflow-hidden border bg-white/50' : 'py-2 mb-2 space-x-4 justify-between'"
                    >
                        <div>
                            <component
                                :is="pageBuilderActive ? 'span' : 'a'"
                                :href="route('courses.feed.customerarea.chapter.show', [course.id, chapter.id])"
                                class="text-inherit cursor-pointer mf-link-immune"
                            >
                                <div
                                    :class="content.displayMode === 'grid' ? 'p-2' : ''"
                                    class="mf-link"
                                >
                                    {{ chapter.title }}
                                </div>
                            </component>
                            <div
                                v-if="props.content.showAuthor || props.content.showDate"
                                class="text-xs opacity-60 cursor-default space-x-1"
                            >
                                <span v-if="props.content.showDate">
                                    {{ chapter.published_at_user_tz_display }}
                                </span>
                                <span v-if="props.content.showDate && props.content.showAuthor">
                                    {{ _mft('shared:by') }}
                                </span>
                                <span v-if="props.content.showAuthor">
                                    {{ chapter.author_name }}
                                </span>
                            </div>

                            <div class="text-xs mt-1">{{ chapter.description }}</div>
                        </div>
                    </li>
                </ul>
                <div
                    v-if="lastPage > 1"
                    class="mt-4 flex items-center justify-center gap-1"
                >
                    <button
                        class="text-xs py-1 px-2"
                        :class="currentPage <= 1 ? 'opacity-40' : ''"
                        :disabled="currentPage <= 1"
                        @click="changePage(currentPage - 1)"
                    >
                        <i class="fa-regular fa-arrow-left"></i>
                    </button>
                    <span class="text-sm opacity-60">
                        {{ currentPage }} / {{ lastPage }}
                    </span>
                    <button
                        class="text-xs py-1 px-2"
                        :class="currentPage >= lastPage ? 'opacity-40' : ''"
                        :disabled="currentPage >= lastPage"
                        @click="changePage(currentPage + 1)"
                    >
                        <i class="fa-regular fa-arrow-right"></i>
                    </button>
                </div>
            </div>
            <div v-else-if="courseLoaded && ! content.hideIfNotAvailable">
                <p>{{ content.courseNotAvailableMessage }}</p>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
